/**
 * components/HaltBanner — the prominent, global "trading is HALTED" banner and the
 * owner re-arm control (the inverse of the kill switch). Rendered by the AppShell
 * whenever the live feed reports a halt, so the halt state is unmissable on EVERY page,
 * not just Overview, and the operator can always resume from the UI.
 *
 * Re-arm is confirm-guarded (a two-step dialog + a server-side confirm token): lifting a
 * halt lets the book trade again, so it must never happen on a single stray click. On
 * success the live feed re-reads `halted=false` and the AppShell unmounts this banner.
 */
import { useState } from 'react';

import { api } from '../api/client';
import { ApiError } from '../api/client';

interface HaltBannerProps {
  /** The halt reason from the live feed, shown so the operator knows what tripped it. */
  readonly reason?: string | null;
}

export function HaltBanner({ reason }: HaltBannerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = (): void => {
    if (busy) return;
    setOpen(false);
    setWhy('');
    setError(null);
  };

  const confirm = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.reArm(why.trim() || 'dashboard re-arm');
      // The live feed will flip halted→false and this banner unmounts; nothing else to do.
      setOpen(false);
      setWhy('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Re-arm request failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="halt-bar" role="alert">
      <span className="halt-bar__dot" aria-hidden />
      <div className="halt-bar__text">
        <strong className="halt-bar__title">Trading halted</strong>
        <span className="halt-bar__reason">{reason || 'Emergency halt engaged — new entries are blocked.'}</span>
      </div>
      <button className="btn btn--primary halt-bar__rearm" type="button" onClick={() => setOpen(true)}>
        Re-arm
      </button>

      {open && (
        <div
          className="kill-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rearm-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div className="kill-dialog kill-dialog--rearm">
            <h2 id="rearm-title">Re-arm trading?</h2>
            <p>
              This clears the durable halt and allows the committee to open new positions again.
              Only re-arm once you have confirmed it is safe to resume.
            </p>

            <div className="field">
              <label htmlFor="rearm-reason">Reason (optional)</label>
              <input
                id="rearm-reason"
                className="input"
                type="text"
                placeholder="e.g. incident resolved, resuming"
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                autoFocus
              />
            </div>

            {error && (
              <div className="banner-error" role="alert">
                {error}
              </div>
            )}

            <div className="kill-dialog__actions">
              <button className="btn btn--ghost" type="button" onClick={close} disabled={busy}>
                Cancel
              </button>
              <button className="btn btn--primary" type="button" onClick={confirm} disabled={busy}>
                {busy ? 'Re-arming…' : 'Confirm re-arm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
